import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import {
  loadTossPayments,
  ANONYMOUS,
  type TossPaymentsWidgets,
  type WidgetAgreementWidget,
  type WidgetPaymentMethodWidget,
} from "@tosspayments/tosspayments-sdk";
import { ME_QUERY } from "../../../api/user-queries";
import { getTossClientKey } from "../../../utils/toss-client-key";
import { notifyError } from "../../../utils/toast";

/**
 * 주문 생성 후 토스 결제위젯을 띄우는 중간 페이지.
 * 쿼리: intent=live-order|pack-today|pack-tomorrow, orderId, amount, orderName(optional)
 */
const TossWidgetCheckoutPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const intent = searchParams.get("intent"); // live-order, pack-today, pack-tomorrow
  const orderId = searchParams.get("orderId");
  const amountStr = searchParams.get("amount");
  const rawOrderName = searchParams.get("orderName");
  const orderNameDisplay = rawOrderName
    ? decodeURIComponent(rawOrderName)
    : "마감히어로 주문";

  const { data: meData, loading: meLoading } = useQuery(ME_QUERY, {
    fetchPolicy: "cache-first",
  });

  const widgetsRef = useRef<TossPaymentsWidgets | null>(null);
  const paymentMethodWidgetRef = useRef<WidgetPaymentMethodWidget | null>(null);
  const agreementWidgetRef = useRef<WidgetAgreementWidget | null>(null);
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const amountValue = amountStr != null ? Math.round(Number(amountStr)) : NaN;

  /** 토스 SDK는 DOM만 비우면 안 되고 destroy()를 호출해야 재렌더 시 "하나의 결제수단 위젯만" 오류가 나지 않음 */
  const destroyWidgetInstances = useCallback(async () => {
    const pm = paymentMethodWidgetRef.current;
    const ag = agreementWidgetRef.current;
    paymentMethodWidgetRef.current = null;
    agreementWidgetRef.current = null;
    widgetsRef.current = null;
    await Promise.all([
      pm?.destroy().catch(() => undefined),
      ag?.destroy().catch(() => undefined),
    ]);
    try {
      const elPm = document.querySelector("#toss-payment-methods");
      const elAg = document.querySelector("#toss-agreement");
      if (elPm) elPm.innerHTML = "";
      if (elAg) elAg.innerHTML = "";
    } catch {
      /* ignore */
    }
  }, []);

  // 결제 UI 렌더링
  useEffect(() => {
    if (meLoading) {
      return;
    }

    if (
      intent !== "live-order" &&
      intent !== "pack-today" &&
      intent !== "pack-tomorrow"
    ) {
      notifyError("잘못된 결제 경로입니다.");
      navigate("/nice-result?ok=0", { replace: true });
      return;
    }
    if (!orderId || !Number.isFinite(amountValue) || amountValue < 1) {
      notifyError("주문 정보가 올바르지 않습니다.");
      navigate("/nice-result?ok=0", { replace: true });
      return;
    }

    const clientKey = getTossClientKey();
    if (!clientKey) {
      notifyError("결제 설정이 완료되지 않았습니다. 고객센터로 문의해 주세요.");
      navigate("/nice-result?ok=0", { replace: true });
      return;
    }

    let cancelled = false;
    // meLoading이 false일 때 한 번만 effect가 돌도록 userId는 의존성에 넣지 않음(미연동 시 중복 renderPaymentMethods 방지)
    const resolvedUserId = meData?.me?.id;
    const customerKey =
      resolvedUserId != null ? `mahi_user_${resolvedUserId}` : ANONYMOUS;

    void (async () => {
      await destroyWidgetInstances();
      try {
        const tossPayments = await loadTossPayments(clientKey);
        if (cancelled) {
          return;
        }
        const widgets = tossPayments.widgets({ customerKey });
        widgetsRef.current = widgets;

        await widgets.setAmount({ currency: "KRW", value: amountValue });
        if (cancelled) {
          return;
        }

        const paymentMethodWidget = await widgets.renderPaymentMethods({
          selector: "#toss-payment-methods",
        });
        if (cancelled) {
          await paymentMethodWidget.destroy().catch(() => undefined);
          return;
        }
        paymentMethodWidgetRef.current = paymentMethodWidget;

        const agreementWidget = await widgets.renderAgreement({
          selector: "#toss-agreement",
        });
        if (cancelled) {
          await paymentMethodWidget.destroy().catch(() => undefined);
          await agreementWidget.destroy().catch(() => undefined);
          return;
        }
        agreementWidgetRef.current = agreementWidget;

        if (!cancelled) {
          setReady(true);
        }
      } catch (e) {
        if (!cancelled) {
          notifyError(
            e instanceof Error ? e.message : "결제 UI를 불러오지 못했습니다.",
          );
          navigate("/nice-result?ok=0", { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
      setReady(false);
      void destroyWidgetInstances();
    };
    // meData/me.id 변경만으로는 재초기화하지 않음(토스 위젯 단일 인스턴스 제약)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    intent,
    orderId,
    amountValue,
    navigate,
    destroyWidgetInstances,
    meLoading,
  ]);

  // 결제 요청
  const handlePay = async () => {
    const widgets = widgetsRef.current;
    if (!widgets || !orderId || !Number.isFinite(amountValue)) {
      return;
    }
    setSubmitting(true);
    const origin = window.location.origin;
    const returnBase = `${origin}/payment/toss/return`;
    const q = `intent=${encodeURIComponent(intent ?? "")}`;
    const successUrl = `${returnBase}?${q}`;
    const failUrl = `${returnBase}?${q}&failed=1`;

    try {
      await widgets.requestPayment({
        orderId,
        orderName: orderNameDisplay,
        successUrl,
        failUrl,
        customerName: meData?.me?.name ?? undefined,
        customerMobilePhone: meData?.me?.phone ?? undefined,
        // SDK 문서(v2)는 taxExemptionAmount(card) 다음에 appScheme을 두어 카드 결제 옵션으로 기술 — 페이북/ISP 복귀용.
        card: { appScheme: "mahi://" } as {
          taxExemptionAmount?: number;
        },
      });
    } catch (e) {
      setSubmitting(false);
      notifyError(e instanceof Error ? e.message : "결제 요청에 실패했습니다.");
    }
  };

  return (
    <div className="min-h-screen bg-white p-4 pb-32">
      <h1 className="text-xl font-bold text-black mb-4">결제</h1>
      <p className="text-sm text-gray-600 mb-4">
        결제 수단과 약관에 동의한 뒤 결제하기를 눌러 주세요.
      </p>
      <div id="toss-payment-methods" className="mb-6" />
      <div id="toss-agreement" className="mb-6" />
      <button
        type="button"
        disabled={!ready || submitting}
        onClick={() => void handlePay()}
        className="w-full py-4 rounded-xl bg-[#1562fc] text-white font-semibold disabled:opacity-50"
      >
        {submitting ? "처리 중…" : "결제하기"}
      </button>
    </div>
  );
};

export default TossWidgetCheckoutPage;
